import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { withRouter } from 'react-router-dom';
import scaleSize from 'utils/scale-size';
import withScrollHack from 'components/HOCs/withScrollHack';
import withMouseControls from 'components/HOCs/withMouseControls';
import { FocusableParent } from '../navigation';
import PageTitle from '../PageTitle';
import GridItem from './GridItem';
import config from '../../config';
import Button from '../Button';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import { CONTENT_TYPES } from 'constants/content-types';
import i18n from '../../i18n';
import withKeepingLastFocus, { LastFocusKeeper } from 'components/HOCs/withKeepingLastFocus';
import { compose } from 'redux';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import PLAYER_TYPES from 'constants/player-types';
import { resolvePlayerNavLink } from 'helpers/urlhelper';

const ROW_HEIGHT = scaleSize(422);

const GridWrapper = styled(FocusableParent)`
  height: ${scaleSize(1080)}px;
  position: relative;
`;

export const GridTitle = styled(PageTitle)``;

export const GridListsWrapper = styled.div`
  height: ${scaleSize(1080)}px;
  padding-top: ${scaleSize(196)}px;
  transform: translate3d(
    0,
    -${({ focusedItemIndex, itemHeight, rowSize }) => scaleSize(itemHeight || ROW_HEIGHT) * Math.floor(focusedItemIndex / rowSize)}px,
    0
  );
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
`;

const GridEditButtonsWrapper = styled(FocusableParent)`
  position: absolute;
  top: 0;
  right: 0;
  text-align: right;
`;

class Grid extends Component {
  state = {
    focusedItemIndex: 0,
    focusedItemId: null,
    editMode: false,
  };

  componentDidMount() {
    const { lastFocusKeeper, gridId } = this.props;

    const lastFocus = lastFocusKeeper.select(gridId);
    if (lastFocus.hasSaved()) {
      lastFocus.apply(lastFocus.reset);
    }
  }

  componentDidUpdate(prevProps) {
    const { items } = this.props;
    const { items: prevItems } = prevProps;

    if (items.length < prevItems.length) {
      SpatialNavigationWrapper.focus();
    }
  }

  onFocus = (item, focusedItemIndex, itemId) => {
    const { onFocus } = this.props;
    onFocus && onFocus(item, focusedItemIndex);
    this.setState({ focusedItemIndex, itemId, focusedItemId: itemId });
  };

  onEnter = (item) => {
    const { onEditItem, showProgress, history, gridId, lastFocusKeeper, navigateToPlayerPage, onEnter, dashboardName } =
      this.props;
    const { focusedItemId } = this.state;
    let itemId = item.targetId || item.id;
    let itemType = item.type || CONTENT_TYPES.VOD_ASSET;
    const isVodChannel = !!item.epgEvent?.channel?.isVod;

    if (this.state.editMode) {
      innerAnalyticsVolia.postEvent({
        eventGroup: `dashboard: ${dashboardName}`,
        eventName: 'edit',
      });

      return onEditItem(item);
    }

    if (typeof onEnter === 'function') return onEnter(item);

    if (showProgress) {
      navigateToPlayerPage({ item });
    } else if (item.epgEventId) {
      const payload = { id: item.channelId, metadata: { startTime: item.eventStartTime } };

      if (item.visibilityDetails === 'NOT_AUTHENTICATED') {
        const onSuccess = resolvePlayerNavLink(payload, {
          type: PLAYER_TYPES.CHANNEL,
          isVodChannel,
        });
        const search = `?channelId=${item.channelId}&onSuccess=${onSuccess}`;
        history.push({ pathname: '/profile/authorization', search });
        return;
      }

      if (item.visibilityDetails === 'MISSING_SUBSCRIPTION') {
        const onSuccess = resolvePlayerNavLink(payload, {
          type: PLAYER_TYPES.CHANNEL,
          isVodChannel,
        });

        history.push({
          pathname: '/profile/packages/filter',
          search: `?channelId=${item.channelId}&onSuccess=${onSuccess}`,
        });
        return;
      }

      navigateToPlayerPage({ item: payload, type: PLAYER_TYPES.CHANNEL });
    } else {
      if (item.metadata && item.metadata.tvSeriesId && itemType === CONTENT_TYPES.VOD_ASSET) {
        itemId = item.metadata.tvSeriesId;
        itemType = CONTENT_TYPES.TV_SERIES;
      }

      let search = '';
      if (item.sourceCarouselId) search += `sourceCarouselId=${item.sourceCarouselId}&`;

      history.push({ pathname: `/details/${itemType}/${itemId}`, search });
    }

    lastFocusKeeper.save(gridId, `#${focusedItemId}`);
  };

  onModalClose = () => {
    const { hideModal } = this.props;
    const { focusedItemId } = this.state;

    const sectionName = `@GRID`;
    const itemIdSelector = `#${focusedItemId}`;
    hideModal();
    SpatialNavigationWrapper.focus(sectionName).focus(itemIdSelector);
  };

  showLoginModal = () => {
    const { showModal, hideModal, navigateToAuthPage } = this.props;
    showModal('CONFIRM', {
      message: i18n.t('common.modal.loginModalTitle'),
      confirmText: i18n.t('common.modal.loginModalConfirm'),
      rejectText: i18n.t('common.modal.loginModalCancel'),
      onModalConfirm: () => {
        hideModal();
        navigateToAuthPage();
      },
      onModalBack: this.onModalClose,
    });
  };

  enableEditMode = () => {
    const { dashboardName } = this.props;

    innerAnalyticsVolia.postEvent({
      eventGroup: `dashboard: ${dashboardName}`,
      eventName: 'click edit',
    });

    this.setState({ editMode: true });
  };

  disableEditMode = () => {
    this.setState({ editMode: false }, () => {
      SpatialNavigationWrapper.focus();
    });
  };

  getRowIndex(index) {
    const { rowSize } = this.props;
    return Math.ceil((index + 1) / rowSize);
  }

  renderContents() {
    const { items, component, withoutContent, itemSize = {}, showProgress, gridId, contentSize } = this.props;
    const { focusedItemIndex, editMode } = this.state;

    const GridItemComponent = component || GridItem;

    if (!items || !items.length) {
      return withoutContent || null;
    }

    const contentSizeFromAssetsType = contentSize;

    return items.map((item, index) => {
      const itemId = `${gridId}-item-${item.id || item.targetId}`;
      return (
        <GridItemComponent
          item={item}
          showProgress={showProgress || item.type === CONTENT_TYPES.VOD_BOOKMARK}
          key={itemId}
          id={itemId}
          itemIndex={index}
          onEnter={(item) => this.onEnter(item)}
          editMode={editMode}
          onFocus={(item, index) => this.onFocus(item, index, itemId)}
          itemSize={itemSize}
          contentSize={contentSizeFromAssetsType}
          hideTitle={this.getRowIndex(focusedItemIndex) > this.getRowIndex(index)}
        />
      );
    });
  }

  renderEditButtons() {
    const { items /* onRemoveAll */ } = this.props;
    const { editMode } = this.state;
    const buttonTitle = i18n.t('common.editButton');

    if (!items || !items.length) {
      return null;
    }

    return (
      <GridEditButtonsWrapper sectionName={'EDIT_MODE'} id={'EDIT_MODE'}>
        {!editMode ? (
          <Button onEnter={this.enableEditMode} id={'edit-button'}>
            {buttonTitle}
          </Button>
        ) : (
          <>
            {/* <Button onEnter={onRemoveAll}>Очистить всё</Button> */}
            <Button onEnter={this.disableEditMode}>{i18n.t('common.editFinished')}</Button>
          </>
        )}
      </GridEditButtonsWrapper>
    );
  }

  render() {
    const { rowSize, itemSize, title, isEditable, gridId, defaultSection = true } = this.props;
    const { focusedItemIndex } = this.state;

    return (
      <GridWrapper className={this.props.className}>
        <GridListsWrapper
          focusedItemIndex={focusedItemIndex}
          rowSize={rowSize}
          itemHeight={itemSize.height + itemSize.metadataHeight}
        >
          {title && <GridTitle>{title}</GridTitle>}
          {isEditable && this.renderEditButtons()}
          <FocusableParent
            defaultSection={defaultSection}
            sectionName={'GRID'}
            id={gridId}
            config={{
              enterTo: 'last-focused',
              leaveFor: {
                right: '',
                up: isEditable ? '@EDIT_MODE' : '',
                down: '',
              },
              debounce: {
                time: 200,
                options: {
                  leading: true,
                  trailing: false,
                  maxWait: 200,
                },
              },
            }}
          >
            {this.renderContents()}
          </FocusableParent>
        </GridListsWrapper>
      </GridWrapper>
    );
  }
}

Grid.defaultProps = {
  rowSize: 4,
  itemSize: {},
};

Grid.propTypes = {
  gridId: PropTypes.string.isRequired,
  lastFocusKeeper: PropTypes.instanceOf(LastFocusKeeper).isRequired,
  rowSize: PropTypes.number,
};

export default compose(withRouter, withKeepingLastFocus, withScrollHack, withMouseControls)(Grid);
